import React, { useMemo } from 'react';
import { getDifficultyStyle, getPurposeStyle, getSourceInfo, formatSearchVolume, intentTypes } from '@/shared/utils/constants';

interface KeywordData {
  keyword: string;
  difficalty?: number | null;
  sv?: number | null;
  intent?: string[];
  purpose?: string[];
  source?: string;
  len?: number;
  parent_keyword?: string;
  [key: string]: any;
}

interface CardViewProps {
  data: KeywordData[];
  groupBy: string;
  quickFilterText: string;
  language: 'fa' | 'en';
  isDark: boolean;
  isRTL: boolean;
  translations: any;
  viewType?: 'list' | 'card';
}

const CardView: React.FC<CardViewProps> = ({ data, groupBy, quickFilterText, language, isDark, isRTL, viewType = 'list' }) => {
  // Filter data based on quickFilterText
  const filteredData = useMemo(() => {
    if (!quickFilterText) return data;
    const searchLower = quickFilterText.toLowerCase();
    return data.filter(item => {
      const keyword = (item.keyword || '').toLowerCase();
      const parentKeyword = (item.parent_keyword || '').toLowerCase();
      return keyword.includes(searchLower) || parentKeyword.includes(searchLower);
    });
  }, [data, quickFilterText]);

  // Group data based on groupBy
  const groupedData = useMemo<Record<string, KeywordData[]>>(() => {
    const groups: Record<string, KeywordData[]> = {};
    
    filteredData.forEach(item => {
      let groupKey = 'other';
      
      if (groupBy === 'intent') {
        if (item.intent && Array.isArray(item.intent) && item.intent.length > 0) {
          groupKey = item.intent[0]; // Use first intent
        } else {
          groupKey = 'no-intent';
        }
      } else if (groupBy === 'difficulty') {
        if (item.difficalty !== null && item.difficalty !== undefined) {
          const style = getDifficultyStyle(item.difficalty);
          groupKey = style ? style.label : 'unknown';
        } else {
          groupKey = 'no-difficulty';
        }
      } else if (groupBy === 'searchVolume') {
        if (item.sv !== null && item.sv !== undefined) {
          if (item.sv < 1000) {
            groupKey = language === 'fa' ? 'کم (زیر 1000)' : 'Low (< 1K)';
          } else if (item.sv < 10000) {
            groupKey = language === 'fa' ? 'متوسط (1K-10K)' : 'Medium (1K-10K)';
          } else if (item.sv < 100000) {
            groupKey = language === 'fa' ? 'زیاد (10K-100K)' : 'High (10K-100K)';
          } else {
            groupKey = language === 'fa' ? 'خیلی زیاد (بالای 100K)' : 'Very High (> 100K)';
          }
        } else {
          groupKey = 'no-volume';
        }
      } else if (groupBy === 'length') {
        groupKey = `${item.len || 0} ${language === 'fa' ? 'کلمه' : 'words'}`;
      } else if (groupBy === 'purpose') {
        if (item.purpose && Array.isArray(item.purpose) && item.purpose.length > 0) {
          const purposeConfig = getPurposeStyle(item.purpose[0]);
          groupKey = purposeConfig ? (language === 'fa' ? purposeConfig.label : purposeConfig.enLabel) : item.purpose[0];
        } else {
          groupKey = 'no-purpose';
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });
    
    return groups;
  }, [filteredData, groupBy, language]);

  // Get group label
  const getGroupLabel = (groupKey: string): string => {
    if (groupBy === 'intent') {
      const intent = intentTypes[groupKey];
      if (intent) {
        return language === 'fa' ? intent.label : intent.enLabel;
      }
      return groupKey === 'no-intent' ? (language === 'fa' ? 'بدون نیت' : 'No Intent') : groupKey;
    } else if (groupBy === 'difficulty') {
      return groupKey === 'no-difficulty' ? (language === 'fa' ? 'بدون سختی' : 'No Difficulty') : groupKey;
    } else if (groupBy === 'searchVolume') {
      return groupKey === 'no-volume' ? (language === 'fa' ? 'بدون حجم جستجو' : 'No Search Volume') : groupKey;
    } else if (groupBy === 'length') {
      return groupKey;
    } else if (groupBy === 'purpose') {
      return groupKey === 'no-purpose' ? (language === 'fa' ? 'بدون هدف' : 'No Purpose') : groupKey;
    } else if (groupBy === 'source') {
      return groupKey === 'no-source' ? (language === 'fa' ? 'بدون منبع' : 'No Source') : groupKey;
    }
    return groupKey;
  };

  // Copy keywords to clipboard
  const copyKeywords = (keywords: KeywordData[]) => {
    const text = keywords.map(k => k.keyword).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      // Show success message (you can add a toast notification here)
      alert(language === 'fa' ? 'کلمات کلیدی کپی شدند' : 'Keywords copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Download keywords as CSV
  const downloadKeywords = (keywords: KeywordData[], groupLabel: string) => {
    const csvContent = [
      ['Keyword', 'Difficulty', 'Search Volume', 'Intent', 'Purpose', 'Length', 'Parent Keyword'].join(','),
      ...keywords.map(k => {
        const difficulty = k.difficalty !== null && k.difficalty !== undefined ? k.difficalty : '';
        const sv = k.sv !== null && k.sv !== undefined ? k.sv : '';
        const intent = Array.isArray(k.intent) ? k.intent.join('; ') : '';
        const purpose = Array.isArray(k.purpose) ? k.purpose.join('; ') : '';
        const len = k.len || '';
        const parent = k.parent_keyword || '';
        return [k.keyword, difficulty, sv, intent, purpose, len, parent].map(v => {
          const str = String(v);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${groupLabel}_keywords_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render based on viewType
  if (viewType === 'card') {
    // Card View - Grid layout with cards side by side (3 columns)
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${isRTL ? 'rtl' : 'ltr'}`}>
        {Object.entries(groupedData).map(([groupKey, keywords]) => {
          return (
            <div 
              key={groupKey}
              className={`rounded-lg shadow-md overflow-hidden relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}
            >
              {/* Card Header with Actions */}
              <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700`}>
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} justify-between items-center gap-2`}>
                  {/* Copy and Download buttons - Left side in RTL, Right side in LTR */}
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-1`}>
                    <button
                      onClick={() => copyKeywords(keywords)}
                      className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-500 dark:text-gray-300`}
                      title={language === 'fa' ? 'کپی کردن' : 'Copy'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => downloadKeywords(keywords, getGroupLabel(groupKey))}
                      className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-500 dark:text-gray-300`}
                      title={language === 'fa' ? 'دانلود CSV' : 'Download CSV'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  </div>
                  {/* Title - Right side in RTL, Left side in LTR */}
                  <h3 className={`text-base font-bold text-gray-900 dark:text-white`}>
                    {getGroupLabel(groupKey)}
                  </h3>
                </div>
              </div>

              {/* Keywords List */}
              <div className="p-4 max-h-96 overflow-y-auto">
                <ul className={`space-y-2 ${isRTL ? 'rtl' : 'ltr'}`}>
                  {keywords.map((item, index) => {
                    // Determine what additional info to show based on groupBy
                    const showDifficulty = groupBy !== 'difficulty' && item.difficalty !== null && item.difficalty !== undefined;
                    const showIntent = groupBy !== 'intent' && item.intent && Array.isArray(item.intent) && item.intent.length > 0;
                    const showSearchVolume = groupBy !== 'searchVolume' && item.sv !== null && item.sv !== undefined;
                    const itemSourceInfo = item.source ? getSourceInfo(item.source) : null;
                    const showSource = groupBy !== 'source' && itemSourceInfo;
                    
                    return (
                      <li 
                        key={`${item.keyword}-${index}`}
                        className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 text-sm text-gray-700 dark:text-gray-300`}
                      >
                        {/* Additional Info - Left side in RTL, Right side in LTR */}
                        <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 flex-shrink-0`}>
                          {/* Difficulty */}
                          {showDifficulty && (
                            <span
                              style={{
                                backgroundColor: getDifficultyStyle(item.difficalty!)?.bgColor || '#e5e7eb',
                                color: getDifficultyStyle(item.difficalty!)?.color || '#374151',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '600',
                                fontSize: '10px'
                              }}
                            >
                              {item.difficalty}
                            </span>
                          )}
                          
                          {/* Search Volume */}
                          {showSearchVolume && (
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              isDark ? 'bg-gray-200 text-gray-700' : 'bg-gray-200 text-gray-700'
                            }`}>
                              {formatSearchVolume(item.sv!)}
                            </span>
                          )}
                          
                          {/* Intent */}
                          {showIntent && item.intent && item.intent.length > 0 && (
                            <span
                              style={{
                                backgroundColor: intentTypes[item.intent[0]]?.bgColor || '#e5e7eb',
                                color: intentTypes[item.intent[0]]?.color || '#374151',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '600',
                                fontSize: '10px'
                              }}
                            >
                              {language === 'fa' 
                                ? intentTypes[item.intent[0]]?.abbreviation 
                                : intentTypes[item.intent[0]]?.enAbbreviation}
                            </span>
                          )}
                          
                          {/* Source Icon */}
                          {showSource && itemSourceInfo && (
                            <span className="flex-shrink-0">
                              {itemSourceInfo.image ? (
                                <img 
                                  src={itemSourceInfo.image} 
                                  alt={language === 'en' ? itemSourceInfo.en : itemSourceInfo.fa} 
                                  className="w-4 h-4 object-contain"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const emojiSpan = target.nextSibling as HTMLElement;
                                    if (emojiSpan) emojiSpan.style.display = 'inline';
                                  }}
                                />
                              ) : null}
                              <span className={`text-sm ${itemSourceInfo.image ? 'hidden' : ''}`}>
                                {itemSourceInfo.icon}
                              </span>
                            </span>
                          )}
                        </div>
                        {/* Keyword Text - Right side in RTL, Left side in LTR */}
                        <span className={`flex-1 break-words ${isRTL ? 'text-right' : 'text-left'}`}>{item.keyword}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // List View - Full width cards stacked vertically
  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {Object.entries(groupedData).map(([groupKey, keywords]) => (
        <div 
          key={groupKey}
          className={`rounded-lg shadow-md overflow-hidden ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          {/* Card Header */}
          <div className={`px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700`}>
            <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} justify-between items-center`}>
              {/* Copy and Download buttons - Left side in RTL, Right side in LTR */}
              <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-2`}>
                <button
                  onClick={() => copyKeywords(keywords)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isDark
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={language === 'fa' ? 'کپی کردن' : 'Copy'}
                >
                  <svg className={`w-5 h-5 inline-block ${isRTL ? 'ml-1' : 'mr-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {language === 'fa' ? 'کپی' : 'Copy'}
                </button>
                <button
                  onClick={() => downloadKeywords(keywords, getGroupLabel(groupKey))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isDark
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={language === 'fa' ? 'دانلود CSV' : 'Download CSV'}
                >
                  <svg className={`w-5 h-5 inline-block ${isRTL ? 'ml-1' : 'mr-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {language === 'fa' ? 'دانلود' : 'Download'}
                </button>
              </div>
              {/* Title - Right side in RTL, Left side in LTR */}
              <h3 className={`text-lg font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {getGroupLabel(groupKey)} ({keywords.length} {language === 'fa' ? 'کلمه' : 'keywords'})
              </h3>
            </div>
          </div>

          {/* Card Body - Keywords List */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {keywords.map((item, index) => (
                <div
                  key={`${item.keyword}-${index}`}
                  className={`p-4 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-800 border-gray-700 hover:border-gray-600' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  } transition-colors`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm mb-2 break-words ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.keyword}
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {item.difficalty !== null && item.difficalty !== undefined && (
                          <span
                            style={{
                              backgroundColor: getDifficultyStyle(item.difficalty)?.bgColor || '#e5e7eb',
                              color: getDifficultyStyle(item.difficalty)?.color || '#374151',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: '600'
                            }}
                          >
                            {item.difficalty}
                          </span>
                        )}
                        {item.sv !== null && item.sv !== undefined && (
                          <span className={`px-2 py-1 rounded ${
                            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {formatSearchVolume(item.sv)}
                          </span>
                        )}
                        {item.intent && Array.isArray(item.intent) && item.intent.length > 0 && (
                          <span
                            style={{
                              backgroundColor: intentTypes[item.intent[0]]?.bgColor || '#e5e7eb',
                              color: intentTypes[item.intent[0]]?.color || '#374151',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: '600',
                              fontSize: '11px'
                            }}
                          >
                            {language === 'fa' 
                              ? intentTypes[item.intent[0]]?.abbreviation 
                              : intentTypes[item.intent[0]]?.enAbbreviation}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CardView;


